import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import ConfirmActionPopup from 'components/ConfirmActionPopup';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { CheckboxSc } from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArrayWithSep } from 'components/final-form/dropdownUtils';
import Input, { StyledInput } from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import { FormState } from 'final-form';
import React, { ChangeEvent, Component, createRef } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import {
  Button,
  Grid,
  Header,
  Icon,
  Input as SemanticInput,
  InputOnChangeData,
  Message,
  Modal
} from 'semantic-ui-react';
import { getAllGuarantorCompanies } from 'services/companyServices';
import {
  deletePoolTestKit,
  registerPoolTestkit,
  searchPoolTestKitRegistrations,
  sendPoolTestKits
} from 'services/poolTestkitServices';
import { getAllTestLaboratory } from 'services/testLaboratoryServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { InterventionBookingStatus } from 'ts-types/api.enums';
import {
  CentralPoolingTestRegistrationSearchRequest,
  PrintLabelDto,
  TestLaboratoryDto,
  TestPoolingRegistrationSearchResult
} from 'ts-types/api.types';
import {
  ChromeExtensionStatus,
  getChromeExtensionStatus,
  getPrinters,
  printLabelDocument
} from 'utils/chomeExtensionUtils';
import { isKeyCheck } from 'utils/keyUtils';
import { applyStyles } from 'utils/textUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledPxGrid = styled(PxGrid)`

  &.ui.grid {
    margin-bottom: .5rem;
  }

  &.ui.grid > .row > .column {
    padding-left: 0;
  }
`;

const PageActionsContainer = styled.div`
  display: flex;
  flex-direction: column;

  .page-actions {
    justify-self: start;
    margin-top: 0.75rem;

    .actions-right {
      float: right;

      .ui.button {
        margin-left: 0.5rem;
      }

      .search-form {
        display: inline-block;
        margin-right: 1rem;

        label {
          margin-right: 1rem;
        }

        .ui.input {
          min-width: 250px;
          margin-right: 1rem;
        }
      }
    }
  }
`;

const StyledHeader = styled(Header)`
  margin-bottom: .5rem;
`;

const StyledCount = styled.span`
  color: rgb(48 145 218);
`;

const InfoDiv = styled.div`
  & .info-message {
    margin-top: 2rem;
    min-height: 105px;
  }

  & .info-message .download-link {
    margin-top: 1.5rem;
  }

  & .actions .button:first-child {
    margin-left: 0;
  }
`;

interface PoolTestkitFieldProps extends FormState {
  values: Partial<CentralPoolingTestRegistrationSearchRequest>

}

interface Props extends RouteComponentProps<any>, AuthConsumerRenderProps, MessageSourceProps {
}

interface State {
  testRegistrationsLoaded: boolean,
  testKitRegistrations: TestPoolingRegistrationSearchResult[],
  selectedTestKitRegistrations: Set<number>,
  numberOfSentTestkitRegistrations?: number,
  laboratories: Array<TestLaboratoryDto>,
  laboratoriesOptions: Array<DropdownOption>,

  companyId?: number,
  laboratoryId?: number,
  companies: Array<DropdownOption>,

  poolTestKitCode: string,

  referenceDataLoaded: boolean,

  printLabelPopupOpen: boolean,
  printLabelPopupStatus: string,

  errorMessages: Array<string>,
  cancelTokenSource: CancelTokenSource;

  poolSubmit: boolean;
}

class PoolTestRegistration extends Component<Props, State> {

  private mounted: boolean = false;

  private poolTestKitCodeInputRef = createRef<SemanticInput>();

  private initialSearchValues: Partial<CentralPoolingTestRegistrationSearchRequest> = {};

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      testRegistrationsLoaded: true,
      testKitRegistrations: [],
      selectedTestKitRegistrations: new Set(),
      laboratories: [],
      laboratoriesOptions: [],
      companies: [],

      poolTestKitCode: "",

      referenceDataLoaded: false,

      printLabelPopupOpen: false,
      printLabelPopupStatus: "none",

      poolSubmit: false,

      errorMessages: [],
      cancelTokenSource
    };

    this.loadReferenceData(cancelTokenSource);

  };

  componentDidMount(): void {
    this.mounted = true;
  };

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  };

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;
    try {

      const companies = await getAllGuarantorCompanies(mandateId, cancelTokenSource);
      const laboratories = await getAllTestLaboratory(cancelTokenSource);

      this.setState({
        companyId: companies[0].id,
        laboratories: laboratories,
        laboratoryId: laboratories.length > 0 ? laboratories[0].id : -1,
        laboratoriesOptions: mapToDropdownOptionArrayWithSep(
            laboratories, ["name", "city"], "id", "id"),
        companies: mapToDropdownOptionArrayWithSep(
            companies, ["description", "city"], "id", "id")
      });

      if (this.state.laboratoryId) {
        this.initialSearchValues = {
          laboratoryId: this.state.laboratoryId,
          mandateId: currentUser!.userConfig.mandateId
        };
      }

    } finally {
      this.setState({
        referenceDataLoaded: true
      });
    }
  }

  fetchTestKitRegistrations = async (values?: Partial<CentralPoolingTestRegistrationSearchRequest>) => {
    const {currentUser} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      testRegistrationsLoaded: false,
      errorMessages: []
    });

    values = {
      mandateId: currentUser!.userConfig.mandateId
    }

    const request = values ? values : this.initialSearchValues;

    try {
      const searchResults = await searchPoolTestKitRegistrations(request, cancelTokenSource);
      this.setState({
        testKitRegistrations: searchResults,
        selectedTestKitRegistrations: new Set(),
        companyId: request.companyId
      });
    } catch (ex) {
      this.handleError(ex);
    } finally {
      this.setState({
        testRegistrationsLoaded: true
      });
    }
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;

    if (error) {
      const errorCode = error.errorCode;
      const knownErrors: Array<string> = [
        "INVALID_BARCODE",
        "BARCODE_ALREADY_EXISTS",
        "UNKNOWN_INTERVENTIONS"
      ];

      const violations: Array<any> = error.violations;

      if (violations && violations.length > 0) {
        violations.forEach(violation => {
          if (knownErrors.includes(violation.errorCode)) {
            this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
          }
        });
      }

      if (!this.state.errorMessages.length) {
        if (knownErrors.includes(errorCode)) {
          this.setErrorMessage(getMessage(`error.${errorCode}`));
        } else {
          this.setErrorMessage(getMessage('error.general'));
        }
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {

    const {errorMessages} = this.state;

    if (errorMessage) {

      const errMsgs = [...errorMessages];
      errMsgs.push(errorMessage);

      this.setState({
        errorMessages: errMsgs
      });
    } else {

      this.setState({
        errorMessages: []
      });
    }
  };

  selectTestRegistration = (testKit: TestPoolingRegistrationSearchResult) => {

    const {selectedTestKitRegistrations} = this.state;

    if (this.isTestKitSelectable(testKit)) {

      let selected = new Set(selectedTestKitRegistrations);

      const bookingId = testKit.poolTestId;
      if (selectedTestKitRegistrations.has(bookingId)) {
        selected.delete(bookingId);
      } else {
        selected.add(bookingId);
      }

      this.setState({
        selectedTestKitRegistrations: selected
      });
    }
  };

  isTestKitSelectable = (testKit: TestPoolingRegistrationSearchResult) => {
    return testKit.interventionBookingStatus === InterventionBookingStatus.EXECUTED && testKit.numTestKits > 0;
  };

  isTestKitSelected = (testKit: TestPoolingRegistrationSearchResult) => {
    const {selectedTestKitRegistrations} = this.state;
    return selectedTestKitRegistrations.has(testKit.poolTestId);
  };

  onEditPoolTestKit = (poolTestKitData: any) => {
    const {companyId} = this.state;
    this.props.history.push("/patient-central/pool-testkit-registration/edit", {
      companyId: companyId,
      poolTestId: poolTestKitData.poolTestId,
      poolTestKitCode: poolTestKitData.testKitCode
    });
  };

  onDeletePoolTestKit = async (poolTestId: number) => {
    await deletePoolTestKit(poolTestId, this.state.cancelTokenSource);
    this.fetchTestKitRegistrations();
  };

  sendExecutedPoolTestKits = async () => {

    const selectedTestIds = this.state.selectedTestKitRegistrations;
    let request = {
      covidTestIds: Array.from(selectedTestIds),
      laboratoryId: this.state.laboratoryId
    };

    let numberSent = 0;
    let result = undefined;
    try {

      result = await sendPoolTestKits(request, this.state.cancelTokenSource);

      // TODO OC: print all label test kits to printer if they are here.
      await this.triggerFastPrintPatientDocuments(result.printLabelDtos);

      this.fetchTestKitRegistrations();

    } finally {
      this.setState({
        selectedTestKitRegistrations: new Set(),
        numberOfSentTestkitRegistrations: result != undefined ? result.numberOfUpdatedTestkits : 0
      });
    }
  };

  triggerFastPrintPatientDocuments = async (documents: PrintLabelDto[]) => {
    const {config} = this.props;

    this.setState({
      printLabelPopupOpen: true,
      printLabelPopupStatus: "STARTED"
    })

    const extStatus = await getChromeExtensionStatus(config);

    if (ChromeExtensionStatus.NO_EXTENSION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OUTDATED_EXTENSION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.NO_HOST_APPLICATION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OUTDATED_HOST_APP === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OK === extStatus) {
      this.setState({
        printLabelPopupStatus: "RETRIEVING_PRINTERS"
      });

      const printersResult: any = await getPrinters();
      const printers = printersResult.payload;
      // @ts-ignore
      const labelPrinter: any = printers.find(printer => {
        const printerName: string = printer.name.toLowerCase();
        return printerName.includes("dymo") && printerName.includes("450");
        //return printerName.includes("microsoft") && printerName.includes("pdf");
      });

      if (labelPrinter) {
        this.setState({
          printLabelPopupStatus: "CREATING_LABELS"
        });

        this.setState({
          printLabelPopupStatus: "PRINTING_LABELS"
        });

        for (const doc of documents) {
          await printLabelDocument(doc, labelPrinter.name);
        }

        this.setState({
          printLabelPopupStatus: "DONE"
        });
      } else {
        this.setState({
          printLabelPopupStatus: "PRINTER_NOT_FOUND"
        });
      }
    }
  };

  closePrintLabelPopup = () => {
    this.setState({
      printLabelPopupOpen: false
    });
  }

  printLabelPopupContent = () => {
    const {getMessage} = this.props;
    const {printLabelPopupStatus} = this.state;

    let content = () => {
      switch (printLabelPopupStatus.toUpperCase()) {
        case 'STARTED' :
          return <div>...</div>;

        case 'NO_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_EXTENSION')}
              <div className="download-link">
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">{getMessage('nativeTools.download.chromeExtension')}</a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_EXTENSION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.chromeExtension')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'NO_HOST_APPLICATION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_HOST_APPLICATION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_HOST_APP' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_HOST_APP')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'PRINTER_NOT_FOUND' :
          return <div>{getMessage("common.labelPrint.printerNotFound")}</div>;

        case 'RETRIEVING_PRINTERS' :
          return <div>{getMessage("common.labelPrint.retrievingPrinters")}</div>;

        case 'CREATING_LABELS' :
          return <div>{getMessage("common.labelPrint.creatingLabels")}</div>;

        case 'PRINTING_LABELS' :
          return <div>{getMessage("common.labelPrint.printingLabels")}</div>;

        case 'DONE' :
          return <div>{getMessage("common.labelPrint.labelsPrinted")}</div>;
      }
    }

    return content();
  }


  setPoolTestKitCode = (evt: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    this.setState({
      poolTestKitCode: data.value
    });
  };

  onPoolTestKitCodeKeyDown = (event: any) => {
    event.persist();
    const isKey: any = isKeyCheck(event);

    if (isKey.enter) {
      this.addNewPoolTestKit();
    }
  };

  addNewPoolTestKit = async () => {
    const {currentUser} = this.props;
    const {poolTestKitCode, cancelTokenSource} = this.state;
    const mandateId = currentUser!.userConfig.mandateId;

    this.setState({
      poolSubmit: true
    });

    try {
      const newPoolTestKit = await registerPoolTestkit(poolTestKitCode, mandateId, cancelTokenSource);
      this.setState({
        poolTestKitCode: ""
      });
      this.onEditPoolTestKit(newPoolTestKit);
    } catch (ex) {
      this.handleError(ex.response.data);
    } finally {
      this.setState({
        poolSubmit: false
      });
    }
  };

  handleChange = debounce(({values}: PoolTestkitFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    if (values.laboratoryId === this.state.laboratoryId) {
      // refresh only if change is not triggered from labaratoryId change
      this.fetchTestKitRegistrations(values);
    } else {
      this.setState({
        laboratoryId: values.laboratoryId
      })
    }

  }, 300);

  render(): React.ReactNode {

    const {getMessage, getMessageWithNamedParams} = this.props;
    const {
      testKitRegistrations,
      selectedTestKitRegistrations,
      numberOfSentTestkitRegistrations,
      poolTestKitCode,
      errorMessages,
      testRegistrationsLoaded,
      laboratoryId,
      laboratories,
      poolSubmit
    } = this.state;

    const state: any = this.props.location.state;
    const registeredTestkitCode = (state && state.registeredTestkitCode) ? state.registeredTestkitCode : undefined;

    const countSelectedTestkits = selectedTestKitRegistrations.size;
    const countSelectableTestkits = testKitRegistrations.filter(this.isTestKitSelectable).length;
    const enableSendExecutedPoolTestKits = selectedTestKitRegistrations.size > 0 && laboratoryId !== undefined;
    let laboratoryName = '';
    if (enableSendExecutedPoolTestKits) {
      const laboratoryMatch = laboratories.filter(lab => lab.id === laboratoryId);
      if (laboratoryMatch.length > 0) {
        laboratoryName = laboratoryMatch[0].name;
      }
    }

    return <PraxStyledSegment style={{height: "100%"}}>

      {
        registeredTestkitCode &&
        <Message success>
          {applyStyles(getMessageWithNamedParams("testKitRegistration.success.registrationSuccess",
              {code: registeredTestkitCode}))}
        </Message>
      }

      {
        numberOfSentTestkitRegistrations && numberOfSentTestkitRegistrations > 0
            ? <Message success>
              {applyStyles(getMessageWithNamedParams("poolTestKitRegistration.success.restRegistrationsSent",
                  {numOfTestkits: numberOfSentTestkitRegistrations}))}
            </Message>
            : null
      }

      {errorMessages.length > 0 &&
      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
        {errorMessages.map(err => <div key={err}>{err}</div>)}
      </StyledErrorMessage>
      }

      <div>
        {this.renderSearchPoolTestkitSegment()}
      </div>

      <div className="selected-testkits">
        {getMessage("poolTestKitRegistration.selectedTestkits")}:&nbsp;
        <strong>
          <StyledCount>{countSelectedTestkits}</StyledCount>/<StyledCount>{countSelectableTestkits}</StyledCount>
        </strong>
      </div>

      <StyledViewTableRow>
        {!testRegistrationsLoaded
            ? <LoaderComponent message={getMessage("poolTestkitEdit.table.loading")}/>
            : this.renderTestKitRegistrationsTable()
        }
      </StyledViewTableRow>

      <PageActionsContainer>
        <div className="page-actions">
          <div className="actions-right">
            <div className="search-form">
              <label>{getMessage("poolTestKitRegistration.addPoolTestKit.label")}:</label>
              <StyledInput
                  ref={this.poolTestKitCodeInputRef}
                  placeholder=""
                  value={poolTestKitCode}
                  onChange={this.setPoolTestKitCode}
                  onKeyDown={(e: any) => this.onPoolTestKitCodeKeyDown(e)}
              />

              <Button
                  type="button"
                  primary
                  onClick={() => this.addNewPoolTestKit()}
                  disabled={poolSubmit || !testRegistrationsLoaded}
              >
                {getMessage("poolTestKitRegistration.addPoolTestKit.addAction")}
              </Button>
            </div>

            <ConfirmActionPopup
                message={getMessageWithNamedParams("poolTestKitRegistration.popup.sendTestKits",
                    {
                      numberOfExecutedTestkits: selectedTestKitRegistrations.size,
                      laboratoryName: laboratoryName
                    })}
                disabled={!enableSendExecutedPoolTestKits}
                renderTarget={<Button
                    type="button"
                    primary
                    icon={{name: "shipping fast"}}
                    disabled={!enableSendExecutedPoolTestKits}
                    content={getMessage("poolTestKitRegistration.button.sendTestKits")}
                />}
                onConfirmAction={() => this.sendExecutedPoolTestKits()}
            />
          </div>
        </div>
      </PageActionsContainer>

      <Modal
          open={this.state.printLabelPopupOpen}
          onClose={() => {
          }}
      >
        <Modal.Header>{getMessage("poolTestKitRegistration.printPopup.title")}</Modal.Header>
        <Modal.Content>
          {this.printLabelPopupContent()}
        </Modal.Content>
        <Modal.Actions>
          <Button type="button" compact onClick={this.closePrintLabelPopup}>{getMessage("button.close")}</Button>
        </Modal.Actions>
      </Modal>

    </PraxStyledSegment>;
  };

  renderSearchPoolTestkitSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialSearchValues}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;
    const {companies, laboratoriesOptions} = this.state;


    return (
        <StyledPxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("company.title")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="companyId"
                  component={Select}
                  options={companies}
                  clearable
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("description")}:
            </Grid.Column>
            <Grid.Column width={6} verticalAlign="middle">
              <Field
                  name="query"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("poolTestKit.laboratory")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="laboratoryId"
                  component={Select}
                  options={laboratoriesOptions}
                  clearable
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </StyledPxGrid>
    );
  };

  renderTestKitRegistrationsTable = (): JSX.Element => {

    const {getMessage} = this.props;
    const {testKitRegistrations} = this.state;

    return (
        <StyledTable
            rowCount={testKitRegistrations.length}
            rowGetter={this.testKitRegistrationsRowGetter}
            rowRenderer={this.testKitRegistrationsRowRenderer}
            columns={[
              {
                width: 60,
                label: this.selectHeaderColumn(),
                dataKey: "index",
                cellRenderer: this.selectableTestRegistrationSelectCellRenderer
              },
              {
                width: 200,
                label: getMessage("poolTestKitRegistration.tableHeader.code"),
                dataKey: "testKitCode",
                flexGrow: 2
              },
              {
                width: 80,
                label: (getMessage("poolTestKitRegistration.tableHeader.numTestKits")),
                dataKey: "numTestKits"
              },
              {
                width: 120,
                label: (getMessage("poolTestKitRegistration.tableHeader.employees")),
                dataKey: "employeeFullNames",
                flexGrow: 3,
                cellRenderer: this.employeesCellRenderer
              },
              {
                width: 150,
                label: (getMessage("poolTestKitRegistration.tableHeader.remarks")),
                dataKey: "testKitId",
                flexGrow: 1,
                cellRenderer: this.remarksCellRenderer
              },
              {
                width: 120,
                label: (getMessage("actions")),
                dataKey: "poolTestId",
                cellRenderer: this.actionCellRenderer
              }
            ]}
        />

    );
  };

  selectHeaderColumn = () => {

    const {testKitRegistrations, selectedTestKitRegistrations} = this.state;

    const countSelectableTestRegistrations =
        testKitRegistrations.filter(this.isTestKitSelectable).length;

    const countSelectedTestRegistrations = selectedTestKitRegistrations.size;
    const indeterminate = countSelectedTestRegistrations > 0
        && countSelectedTestRegistrations < countSelectableTestRegistrations;
    const noSelectedTestRegistrations = countSelectedTestRegistrations === 0
        && countSelectableTestRegistrations > 0;
    const allTestRegistrationsSelected = countSelectableTestRegistrations > 0
        && countSelectedTestRegistrations === countSelectableTestRegistrations;

    return (
        <CheckboxSc
            checked={countSelectedTestRegistrations > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedTestRegistrations || indeterminate) {
                  const covidTestIds = testKitRegistrations
                      .filter(this.isTestKitSelectable)
                      .map(tr => tr.poolTestId);

                  this.setState({
                    selectedTestKitRegistrations: new Set(covidTestIds)
                  });
                }

                if (allTestRegistrationsSelected) {
                  this.setState({
                    selectedTestKitRegistrations: new Set()
                  });
                }

              }
            }
            disabled={countSelectableTestRegistrations === 0}
        />
    );
  };

  selectableTestRegistrationSelectCellRenderer = (data: any) => {

    const {rowData} = data;
    const testKit: TestPoolingRegistrationSearchResult = rowData;

    if (this.isTestKitSelectable(testKit)) {
      return (
          <CheckboxSc
              checked={this.isTestKitSelected(testKit)}
              onChange={() => this.selectTestRegistration(testKit)}
          />
      );
    }

    return <></>;
  };

  testKitRegistrationsRowGetter = (
      {
        index
      }
          : any) => {
    const {testKitRegistrations} = this.state;
    Object.assign(testKitRegistrations[index], {index: index + 1});

    return testKitRegistrations[index];
  }
  ;

  testKitRegistrationsRowRenderer = (
      {
        className, columns, index, key, style
      }
          : any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    // const {testKitRegistrations} = this.state;
    // const registration = testKitRegistrations[index];

    let rowStyle = {...style};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={rowStyle}
        >
          {columns}
        </div>
    );
  };

  employeesCellRenderer = (data: any) => {
    const {rowData} = data;
    const employeeNamesList = rowData.employeeFullNames.join(", ");
    return <div title={employeeNamesList}>
      {
        employeeNamesList
      }
    </div>;
  };

  remarksCellRenderer = (data: any) => {
    return "";
  };

  actionCellRenderer = (
      {
        rowData
      }
          : any) => {
    const {getMessageWithNamedParams, getMessage} = this.props;

    return <div className="row-actions">
      <EditActionButton
          hoverMessage={getMessage('msgEdit')}
          onConfirm={() => this.onEditPoolTestKit({...rowData})}
      />
      <DeleteActionButton
          popupMessage={
            getMessageWithNamedParams("poolTestKitRegistration.action.deleteConfirmMessage",
                {
                  code: rowData.testKitCode
                })
          }
          hoverMessage={getMessage("testKitRegistration.action.deleteHoverMessage")}
          onConfirm={() => this.onDeletePoolTestKit(rowData.poolTestId)}
          trigger={<Icon name={'trash alternate outline'}/>}
      />
    </div>;
  };

}

export default withRouterWorkaround(
    withAuthContext(
        withMessages(
            PoolTestRegistration)));
